import { css } from 'aphrodite';
import { observer, Provider } from 'mobx-react';
import React, { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { IImpersonationContextComponentProps } from '../../../../models';
import { MastheadPortalId } from '../../../../models/AdminModels';
import { useAdminUserSession } from '../../../../models/hooks/adminStateHooks';
import { openUrlInNewTab } from '../../../../models/UiUtils';
import { useAdminAccountQuery } from '../../../../queries';
import { AccountOperationsViewModel, ImpersonationContextViewModel } from '../../../../viewmodels/AdminViewModels';
import { BlogRssUploadModal } from '../../../../web/components/blog/BlogRssUpload';
import { SitemapToRssModal } from '../../../../web/components/blog/SitemapToRssModal';
import { MultiContainerHeader } from '../../../../web/components/MultiContainerHeader';
import { BlogIcon } from '../../../../web/components/svgs/icons/BlogIcon';
import { HistoryIcon } from '../../../../web/components/svgs/icons/HistoryIcon';
import { SparklesIcon } from '../../../../web/components/svgs/icons/SparklesIcon';
import { WritebackIcon } from '../../../../web/components/svgs/icons/WritebackIcon';
import { prestoPurple } from '../../../../web/styles/colors';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { CreateBlogDomain } from '../../../components/blogs';
import { BreadcrumbsBarWithNameProvider } from '../../../components/blogs/BreadcrumbsBarWithNameProvider';
import { styleSheet } from './styles';

export const BlogSettings = observer(function BlogSettings() {
	const userSession = useAdminUserSession();
	const history = useHistory();
	const { id: accountId } = useParams<{ id: string }>(); // Assuming the route param is 'id'
	const impersonationContextComponentPropsRef = React.useRef<IImpersonationContextComponentProps>({
		impersonationContext: new ImpersonationContextViewModel({
			account: { id: accountId },
		}),
	});
	const [accountOperations, setAccountOperations] = React.useState<AccountOperationsViewModel | null>(null);
	const [showBlogRssUpload, setShowBlogRssUpload] = React.useState(false);
	const [showSitemapToRss, setShowSitemapToRss] = React.useState(false);

	const { data: account } = useAdminAccountQuery({
		accountId,
		meta: {
			toastErrorMessage: 'Error loading account',
		},
	});

	useEffect(() => {
		if (account) {
			impersonationContextComponentPropsRef.current.impersonationContext.update({
				account,
			});
			setAccountOperations(new AccountOperationsViewModel(userSession, account));
		}
	}, [account, userSession]);

	const onBlogEditorClicked = () => {
		const { origin, pathname } = window.location;
		openUrlInNewTab(`${origin}${pathname}#/accounts/${account?.id}/blog-editor`);
	};

	const onBlogEditorV2Clicked = () => {
		const { origin, pathname } = window.location;
		openUrlInNewTab(`${origin}${pathname}#/accounts/${account?.id}/template-designer`);
	};

	const onBlogRssUploadRequestClose = () => {
		setShowBlogRssUpload(false);
	};

	const onSitemapToRssRequestClose = () => {
		setShowSitemapToRss(false);
	};

	const onViewImportHistoryClicked = () => {
		history.push(`/accounts/${accountId}/blog-import-history`);
	};

	const blogSettingsEnabled = accountOperations?.account?.features?.blogFeature?.domain;

	return (
		<div className='admin-blog-settings-container'>
			<MultiContainerHeader
				appBarHeader={<BreadcrumbsBarWithNameProvider account={account} currentLocation='Blog Settings' />}
				appBarPortalDestinationId={MastheadPortalId}
			/>
			{account ? (
				<Provider {...impersonationContextComponentPropsRef.current}>
					<div className={css(styleSheet.actionBar)}>
						<button
							className={css(baseStyleSheet.horizontalStack, styleSheet.actionBarButton)}
							disabled={!blogSettingsEnabled}
							onClick={onBlogEditorV2Clicked}
						>
							<BlogIcon fill={prestoPurple} />
							<span>Template Designer</span>
						</button>
						<button
							className={css(baseStyleSheet.horizontalStack, styleSheet.actionBarButton)}
							disabled={!blogSettingsEnabled}
							onClick={onBlogEditorClicked}
						>
							<BlogIcon fill={prestoPurple} />
							<span>Manual Template Editor</span>
						</button>
						<button
							className={css(baseStyleSheet.horizontalStack, styleSheet.actionBarButton)}
							disabled={!blogSettingsEnabled}
							onClick={() => setShowBlogRssUpload(true)}
						>
							<WritebackIcon fillColor={prestoPurple} />
							<span>Import Blog Posts</span>
						</button>
						<button
							className={css(baseStyleSheet.horizontalStack, styleSheet.actionBarButton)}
							disabled={!blogSettingsEnabled}
							onClick={() => setShowSitemapToRss(true)}
						>
							<SparklesIcon style={{ color: prestoPurple }} />
							<span>Sitemap to RSS</span>
						</button>
						<button
							className={css(baseStyleSheet.horizontalStack, styleSheet.actionBarButton)}
							disabled={!blogSettingsEnabled}
							onClick={onViewImportHistoryClicked}
						>
							<HistoryIcon fillColor={prestoPurple} />
							<span>View Import History</span>
						</button>
					</div>
					{accountOperations ? <CreateBlogDomain accountOperations={accountOperations} /> : null}
					<BlogRssUploadModal
						modalProps={{
							isOpen: showBlogRssUpload,
							onRequestClose: () => setShowBlogRssUpload(false),
						}}
						onFinish={onBlogRssUploadRequestClose}
					/>
					<SitemapToRssModal
						modalProps={{
							isOpen: showSitemapToRss,
							onRequestClose: () => setShowSitemapToRss(false),
						}}
						onFinish={onSitemapToRssRequestClose}
					/>
				</Provider>
			) : null}
		</div>
	);
});
